import { gql } from '@apollo/client'
import { BoxLockVariationType } from '@jusbrasil-web/shared-apollo-client/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef, useState } from 'react'

import { BenefitsLockContent } from '../benefits-modal-content'
import {
  CaseTrackingBgCheckFromBgCheckOffersLock_OfferLockDataFragment,
  CaseTrackingBgCheckFromBgCheckOffersLock_ViewerFragment,
} from './__generated__/case-tracking-bg-check-from-bg-check-offers-lock.graphql'
import { CaseTrackingBGCheckFromBgCheckOfferContent } from './case-tracking-bg-check-from-bg-check-offer-content'
import { ModalLock } from './modal-lock'

export interface CaseTrackingBgCheckFromBgCheckOffersLockProps {
  lockVariationType: BoxLockVariationType
  offerLockData: CaseTrackingBgCheckFromBgCheckOffersLock_OfferLockDataFragment
  viewer: CaseTrackingBgCheckFromBgCheckOffersLock_ViewerFragment
}

export function CaseTrackingBgCheckFromBgCheckOffersLock({
  lockVariationType,
  offerLockData,
  viewer,
}: CaseTrackingBgCheckFromBgCheckOffersLockProps) {
  const [step, setStep] = useState<'info' | 'offer'>('info')

  const eventProperties = useMemo(() => {
    const lockEventProperties = offerLockData?.body?.cards?.[0]?.lockEventProperties
    if (!lockEventProperties) return undefined

    return {
      app_id: lockEventProperties.app_id,
      from_component: lockEventProperties.from_component,
      feature: lockEventProperties.feature,
      next_plan: lockEventProperties.next_plan,
      current_plan: lockEventProperties.current_plan,
      pid: viewer?.pid,
    }
  }, [offerLockData?.body?.cards, viewer?.pid])

  const hasSentViewMetrics = useRef(false)

  useEffect(() => {
    if (hasSentViewMetrics.current) return
    hasSentViewMetrics.current = true

    sendMetrics(
      'Box.BenefitsCaseTrackingBgCheckFromBgCheckLockView',
      eventProperties?.pid?.toString() ?? null,
      { ...(eventProperties || {}), modal_type: lockVariationType }
    )
  }, [lockVariationType, eventProperties])

  const handleOnClick = () => {
    setStep('offer')
  }

  return (
    <ModalLock>
      {step === 'info' && (
        <BenefitsLockContent
          lockVariationType={lockVariationType}
          handleOnClick={handleOnClick}
          eventProperties={eventProperties}
        />
      )}
      {step === 'offer' && (
        <CaseTrackingBGCheckFromBgCheckOfferContent offerLockData={offerLockData} viewer={viewer} />
      )}
    </ModalLock>
  )
}

CaseTrackingBgCheckFromBgCheckOffersLock.fragments = {
  offerLockData: gql`
    fragment CaseTrackingBgCheckFromBgCheckOffersLock_offerLockData on OfferLockData {
      ...CaseTrackingBGCheckFromBgCheckOfferContent_offerLockData
    }
    ${CaseTrackingBGCheckFromBgCheckOfferContent.fragments.offerLockData}
  `,
  viewer: gql`
    fragment CaseTrackingBgCheckFromBgCheckOffersLock_viewer on Profile {
      pid
      ...CaseTrackingBGCheckFromBgCheckOfferContent_viewer
    }
    ${CaseTrackingBGCheckFromBgCheckOfferContent.fragments.viewer}
  `,
}
